import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  CUSTOMER_MEMBER_ROLE,
  DASHBOARD_ROLES,
  FILE_UPLOAD_MAX_SIZE,
  GALLERY,
  IMAGE_FILE_TYPES,
  NOTE,
  SEVERE,
  VIDEO,
} from '@careapp-group/core';
import { CustomerDto, MemoResponseDto } from '@careapp-group/ng-sdk-v2-new';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { defaultIfEmpty, distinctUntilChanged, filter, map, pluck, take } from 'rxjs/operators';
import * as actions from '../../../store/actions';
import { MultiCustomerContentCreation } from '../../customer-content-creation/customer-content-creation';
import { FileIconComponent } from '../../file-icon/file-icon.component';
import { requiredAndNotEmpty } from '../../validators';
import { MEMOS, MEMO_CATEGORIES } from '../../../store';
import { getSelfCustomer } from '../../../store/customers/customers.selectors';
import {
  getFileUploadProgress,
  getFileUploadError,
} from '../../../store/file-upload/file-upload-selectors';
import * as selectors from '../../../store/reducers';
import { getIsFamily } from '../../../store/organisations/organisations.selectors';
import { environment } from '../../../../environments/environment';
import { VideoProcessingService } from '../../../organisation/create/video-processing.service';
import { allowPhotos, FileDownloadUrlPipeV2 } from '../../pipes';

@Component({
  selector: 'ca-memo-form',
  templateUrl: './memo-form.component.html',
  styleUrls: ['./memo-form.component.scss'],
})
export class MemoFormComponent extends MultiCustomerContentCreation implements OnInit, OnDestroy {
  public isFamily$: Observable<boolean>;

  get customerListIds(): string[] {
    return this.form.get('customerListIds').value;
  }
  set customerListIds(customerListIds: string[]) {
    this.form.patchValue({
      customerListIds,
    });
    this.changeDetector.detectChanges();
  }

  readonly MAX_AVATAR_COUNT = 3;
  IMAGE_TYPES = IMAGE_FILE_TYPES.join(',');

  public get mediaItems() {
    return this._mediaItems;
  }
  public set mediaItems(data) {
    if (!data) {
      return;
    }
    this._mediaItems = data;
    this.form.patchValue({
      fileIds: data.filter((p) => !!p.id).map((p) => p.id),
    });
  }

  get customerIds() {
    return this.form.get('customerIds').value;
  }

  set customerIds(customerIds: string[]) {
    this.form.patchValue({
      customerIds,
    });
    this.changeDetector.detectChanges();
  }

  get options() {
    const type = this.form.get('type').value;
    return {
      ...super.options,
      disableNoMedia: type === GALLERY || type === VIDEO,
    };
  }

  constructor(
    private changeDetector: ChangeDetectorRef,
    protected dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    protected store: Store<any>,
    private video: VideoProcessingService,
    protected viewContainerRef: ViewContainerRef,
    public downloadUrlPipe: FileDownloadUrlPipeV2,
    @Inject('Window') private window: Window & typeof globalThis
  ) {
    super(dialog, store, viewContainerRef, route.snapshot.queryParamMap.get('customerId'));
  }
  public types = {
    NOTE: NOTE,
    GALLERY: GALLERY,
    VIDEO: VIDEO,
  };
  public typeSubscription: Subscription;
  public isFamilySubscription: Subscription;
  public title$: Observable<string>;

  public SEVERE = SEVERE;

  _mediaItems: { id: string; url?: string; file: File; progress$: Observable<number> }[] = [];

  public selected: string;

  public form: UntypedFormGroup;

  public loading$: Observable<any>;
  public memoCategories$: Observable<any>;
  public memoCategoriesLoading$: Observable<boolean>;
  public organisationPreferences$: Observable<any>;
  public customerSelf$: Observable<CustomerDto>;
  private _existingMemo: MemoResponseDto;

  @Input() set existingMemo(message: MemoResponseDto) {
    if (!message) {
      return;
    }
    this._existingMemo = message;

    this.store.dispatch(actions.Customers.RequestAllAction());
    this.store.dispatch(actions.MemoCategories.RequestAllAction());

    // Retrieve all customers in the provided memo.
    for (const customerId of message.customerIds ?? []) {
      this.store.dispatch(actions.Customers.RequestByIdAction({ id: customerId }));
    }

    this.initialiseFormIfNull();

    this.form.patchValue({
      customerListIds: [],
      customerIds: message.customerIds || [],
      type: message.type,
      body: message.shared ? message.sharedBody : message.body,
      shared: message.shared || false,
      categoryIds: message.categories?.map((c) => c.id) || [],
      fileIds: message.media?.map((m) => m.id) || [],
    });

    if (message.type === GALLERY || message.type === VIDEO) {
      this.form.get('fileIds').setValidators([requiredAndNotEmpty]);
      this.form.get('fileIds').updateValueAndValidity();
    } else {
      this.form.get('fileIds').setValidators([]);
    }

    const mediaItems = [];
    message.media.forEach((fileRecord) => {
      this.downloadUrlPipe.getDownloadUrl(fileRecord).subscribe((url) => {
        mediaItems.push({
          file: fileRecord,
          id: fileRecord.id,
          progress$: of(100),
          url,
        });

        if (mediaItems.length === message.media.length) {
          this.mediaItems = mediaItems;
          this.changeDetector.detectChanges(); // Ensure change detection runs
        }
      });
    });

    this.changeDetector.detectChanges();

    this.setInitialCustomers(message.customerIds ?? []);
    this.changeDetector.detectChanges();
  }

  get existingMemo() {
    return this._existingMemo;
  }

  @Input()
  submissionEnabled: boolean = true;

  @Output()
  onSubmit = new EventEmitter<{
    customerListIds: string[];
    customerIds: string[];
    type: string;
    body: string;
    shared: boolean;
    categoryIds: string[];
    fileIds: string[];
  }>();

  @ViewChild('confirmationDialog', { static: true, read: TemplateRef })
  confirmationDialog: TemplateRef<any>;

  DASHBOARD_ROLES = DASHBOARD_ROLES;
  DASHBOARD_AND_CUSTOMER_ROLES = [...DASHBOARD_ROLES, CUSTOMER_MEMBER_ROLE];

  ngOnInit() {
    const initialId = this.route.snapshot.queryParamMap.get('customerId');
    this.initialiseFormIfNull();
    this.form.patchValue({ customerIds: initialId ? [initialId] : [] });
    this.typeSubscription = this.route.data
      .pipe(pluck('type'), defaultIfEmpty(NOTE))
      .subscribe((type) => {
        this.form.patchValue({ type: type });
        if (type === GALLERY || type === VIDEO) {
          this.form.get('fileIds').setValidators([requiredAndNotEmpty]);
          this.form.get('fileIds').updateValueAndValidity();
        } else {
          this.form.get('fileIds').setValidators([]);
        }
      });
    this.title$ = this.route.data.pipe(pluck('title'));

    this.loading$ = this.store.select(selectors.getCreating(MEMOS));
    this.customerSelf$ = this.store.select(getSelfCustomer).pipe(filter((customer) => !!customer));
    this.memoCategories$ = this.store.select(selectors.getCurrentOrganisationCategories);
    this.memoCategoriesLoading$ = this.store.select(selectors.getLoadingAll(MEMO_CATEGORIES));
    // Ensure we get the current organisation's categories
    this.store.dispatch(actions.MemoCategories.RequestByIdAction({ id: '' }));
    this.organisationPreferences$ = this.store.select(selectors.org.currentOrgPreferences);
    this.isFamily$ = this.store.select(getIsFamily);

    this.isFamilySubscription = this.isFamily$
      .pipe(distinctUntilChanged())
      .subscribe((isFamily) => {
        if (isFamily) {
          this.form.patchValue({ shared: true });
        }
      });
  }

  // If no form exists, will create the form. Otherwise, leaves as is.
  initialiseFormIfNull() {
    if (!this.form) {
      this.form = this.formBuilder.group({
        customerListIds: this.formBuilder.control([]),
        customerIds: this.formBuilder.control([], requiredAndNotEmpty),
        type: this.formBuilder.control(NOTE, requiredAndNotEmpty),
        body: this.formBuilder.control(null, requiredAndNotEmpty),
        shared: this.formBuilder.control(false),
        categoryIds: this.formBuilder.control([], requiredAndNotEmpty),
        fileIds: this.formBuilder.control([]),
      });
    }
  }

  ngOnDestroy() {
    if (this.isFamilySubscription) {
      this.isFamilySubscription.unsubscribe();
    }
    if (this.typeSubscription) {
      this.typeSubscription.unsubscribe();
      this.typeSubscription = undefined;
    }
    // Release any temporary blob urls created
    this.mediaItems.forEach((item) => {
      if (item.url && /^(blob:)/.test(item.url)) {
        this.window.URL.revokeObjectURL(item.url);
      }
    });
  }

  async submitMemo() {
    const memo = { ...this.form.value };
    const isGalleryOrVideo = ![GALLERY, VIDEO].includes(memo.type);
    let customers = await this.customers$.pipe(take(1)).toPromise();

    if (isGalleryOrVideo) {
      customers = customers.filter((customer) => customer.settings?.allowMemoImages !== false);
    }

    if ((customers && !allowPhotos(customers)) || isGalleryOrVideo) {
      memo.fileIds = [];
    }
    this.onSubmit.emit(this.form.value);
  }

  typeChanged(type) {
    this.form.patchValue({
      type,
    });

    if (type !== SEVERE) {
      return this.form.patchValue({ confirmSevereConcern: true });
    }

    if (type === SEVERE) {
      return this.form.patchValue({ confirmSevereConcern: false });
    }
  }

  categoriesChanged(values) {
    this.form.patchValue({
      categoryIds: values,
    });
  }

  addFile({ file, url, type }: { file: File; url: string; type: 'image' | 'video' }) {
    if (file.size >= FILE_UPLOAD_MAX_SIZE) {
      const maxSize = Math.floor(FILE_UPLOAD_MAX_SIZE / 1024 / 1024);
      this.store.dispatch(
        actions.ShowSnackBar({
          message: `Selected file was too large - maximum size is ${maxSize}MB`,
        })
      );
      return;
    }

    const id = (((1 + Math.random()) * 0x10000) | 0).toString(16); // Random 5 character hex string
    this.store.dispatch(actions.RequestFileUploadStartAction({ id, file }));

    const item = {
      id,
      file,
      type,
      url,
      progress$: combineLatest(
        this.store.select(getFileUploadProgress(id)),
        this.store.select(getFileUploadError(id))
      ).pipe(
        map(([progress, error]) => {
          if (error) {
            this.removeMedia(id);
            return 100;
          }
          return progress;
        })
      ),
    };

    this.mediaItems = [...this._mediaItems, item];
    this.changeDetector.detectChanges();
  }

  addPhotos(files: File[]) {
    for (let file of files) {
      this.addFile({
        file,
        url: this.window.URL.createObjectURL(file),
        type: 'image',
      });
    }
  }

  async addVideo(videos: File[]) {
    if (videos.length !== 1) {
      return;
    }
    const video = videos[0];
    const duration = await this.video.getVideoDuration(video);
    if (duration > environment.maxVideoUploadLengthS) {
      this.store.dispatch(
        actions.ShowSnackBar({
          message: `Video too long - must be less than ${
            environment.maxVideoUploadLengthS / 60.0
          } minutes`,
        })
      );
      return;
    }

    let thumbnail: string;
    try {
      thumbnail = await this.video.generateThumbnail(video, {
        width: 150,
        height: 150,
      });
    } catch (err) {
      thumbnail = FileIconComponent.iconUrlForMimeType(video.type);
    }
    this.addFile({
      file: video,
      url: thumbnail,
      type: 'video',
    });
  }

  removeMedia(id: string) {
    this.mediaItems = this._mediaItems.filter((item) => item.id !== id);
    this.changeDetector.detectChanges();
  }

  async promptRemove(id: string) {
    const dialogRef = this.dialog.open(this.confirmationDialog);
    const result = await dialogRef.afterClosed().toPromise();
    if (result) {
      this.removeMedia(id);
    }
  }
}
